import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { useMemo } from "react";
import type { UseSelector } from "react-redux";
import { useDispatch as useReduxDispatch, useSelector as useReduxSelector } from "react-redux";

import type { RootState } from "types/redux";

type UseMemoisedSelectorOverloads = {
  <Argument, Result>(
    argumentSelector: (...args: never[]) => Argument,
    selectorFactory: (arg: Argument) => (...args: never[]) => Result,
  ): Result;
  <Argument, Result, TransformedArgument>(
    argumentSelector: (...args: never[]) => Argument,
    selectorFactory: (arg: TransformedArgument) => (...args: never[]) => Result,
    transformArgument: (arg: Argument) => TransformedArgument,
  ): Result;
};

export const useDispatch: () => (
  action: UnknownAction | ThunkAction<unknown, never, unknown, UnknownAction>,
) => ReturnType<typeof useReduxDispatch> = useReduxDispatch as typeof useDispatch;

export const useSelector: UseSelector<RootState> = useReduxSelector;

export const useMemoisedSelector: UseMemoisedSelectorOverloads = <Argument, Result, TransformedArgument>(
  argumentSelector: (...args: never[]) => Argument,
  selectorFactory: (arg: Argument | TransformedArgument) => (...args: never[]) => Result,
  transformArgument?: (arg: Argument) => TransformedArgument,
) => {
  const argument = useSelector(argumentSelector);

  const memoisedSelector = useMemo(() => {
    const transformedArgument = transformArgument === undefined ? argument : transformArgument(argument);
    return selectorFactory(transformedArgument);
  }, [argument, selectorFactory, transformArgument]);

  return useSelector(memoisedSelector);
};

export const coalesceArray = <T>(value: T[] | undefined) => value ?? [];

export const transformKnowledgePackage = <T>(item: { data: T[] } | undefined) => item?.data ?? [];
